import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import CalendarTodayOutlined from '@material-ui/icons/CalendarTodayOutlined';
import ChatBubbleOutline from '@material-ui/icons/ChatBubbleOutline';
import EmojiEventsOutlined from '@material-ui/icons/EmojiEventsOutlined';
import FileCopyOutlined from '@material-ui/icons/FileCopyOutlined';
import SettingsOutlined from '@material-ui/icons/SettingsOutlined';
import Alert from '../../../components/common/Alert';
import BreadCrumb from '../../../components/Breadcrumbs';
import Stepper from '../../../components/common/stepper';
import useSnackbar from '../../../hooks/useSnackbar';
import PremioAPI from '../premio-api';
import { getPremioWebUrl } from '../premio-urls';
import BrandingEditor, { Field } from './BrandingEditor';
import FormSchemaEditor, { validateFormSchema } from './FormSchemaEditor';
import PremioPreview from './PremioPreview';
import { apiErrorMessage, availableCopySlug, buildPayload, fromApi, getApplicationParts, newConvocatoria, setChatEnabled, slugify } from './editor-model';
import '../../../assets/styles/content/premio-editor.scss';

const STEPS = [
  { title: 'Inicio de sesión', description: 'Define la vigencia, los textos y las bases que verán las personas antes de postular.' },
  { title: 'Logos e identidad visual', description: 'Personaliza los logos, la imagen de portada y los colores del premio.' },
  { title: 'Preguntas del Formulario 1', description: 'Organiza las secciones y las preguntas que completarán las personas al postular.' },
];
const STATUS = {
  draft: { label: 'Borrador', className: 'waiting', help: 'Para recibir postulaciones, selecciona Activado y guarda los cambios al finalizar.' },
  open: { label: 'Activado', className: 'active', help: 'Al guardar, el premio podrá recibir postulaciones dentro de las fechas de inicio y término.' },
  closed: { label: 'Cerrado', className: 'waiting', help: 'El premio no recibe postulaciones. Puedes volver a activarlo y revisar sus fechas.' },
  archived: { label: 'Archivado', className: 'waiting', help: 'El premio está archivado y no recibe postulaciones.' },
};

const ConvocatoriaEditor = () => {
  const { convocatoriaId } = useParams();
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const isEditing = !!convocatoriaId;
  const [value, setValue] = useState(newConvocatoria);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [formChanged, setFormChanged] = useState(false);
  const [error, setError] = useState('');
  const [errorSeverity, setErrorSeverity] = useState('error');
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [fileUploadLimits, setFileUploadLimits] = useState(undefined);
  const [activeStep, setActiveStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const panels = useRef([]);
  const busy = loading || saving || uploading;
  const title = isEditing ? 'Editar premio' : 'Crear premio';
  const reportProblem = useCallback((message, severity = 'error') => {
    setError(message);
    setErrorSeverity(severity);
    showSnackbar(message, severity);
  }, [showSnackbar]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setLoaded(false);
    const load = async () => {
      try {
        if (convocatoriaId) {
          const response = await PremioAPI.fetchConvocatoria(convocatoriaId);
          if (!cancelled) {
            setValue(fromApi(response.data));
            setFileUploadLimits(response.data.file_upload_limits);
          }
        } else {
          const [form, list] = await Promise.all([PremioAPI.fetchFormTemplate(), PremioAPI.fetchConvocatorias()]);
          if (!cancelled) {
            setValue({ ...newConvocatoria(), form_schema: form.data });
            setFileUploadLimits(form.data.file_upload_limits);
            setTemplates(list.data || []);
          }
        }
        if (!cancelled) { setLoaded(true); setDirty(false); setFormChanged(false); setActiveStep(0); }
      } catch (loadError) {
        if (!cancelled) reportProblem(apiErrorMessage(loadError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [convocatoriaId, loadAttempt, reportProblem]);

  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [dirty]);

  const change = (changes) => { setValue((current) => ({ ...current, ...changes })); setDirty(true); };
  const copyTemplate = async () => {
    if (!templateId || (dirty && !window.confirm('Se reemplazarán los cambios de este premio con la configuración seleccionada. ¿Continuar?'))) return;
    setLoading(true);
    setError('');
    try {
      const [response, list] = await Promise.all([
        PremioAPI.fetchConvocatoria(templateId), PremioAPI.fetchConvocatorias(),
      ]);
      const template = fromApi(response.data);
      const existing = list.data || [];
      setTemplates(existing);
      const { id, created_at, updated_at, ...copy } = template;
      change({ ...copy, title: `${template.title} (copia)`.slice(0, 255), slug: availableCopySlug(template.slug, existing.map(item => item.slug)), status: 'draft' });
      setFileUploadLimits(response.data.file_upload_limits);
      setFormChanged(true);
      showSnackbar('Configuración copiada. Revisa los datos y guarda el premio para confirmar los cambios.', 'success');
    } catch (copyError) {
      reportProblem(apiErrorMessage(copyError));
    } finally {
      setLoading(false);
    }
  };

  const revealInvalidField = (event) => {
    // Wait until the owning step and collapsed question are visible before focusing.
    event.preventDefault();
    const input = event.target;
    const panel = input.closest('[data-editor-step]');
    if (panel) setActiveStep(Number(panel.dataset.editorStep));
    let parent = input.parentElement;
    while (parent) {
      if (parent.tagName === 'DETAILS') parent.open = true;
      parent = parent.parentElement;
    }
    const label = input.getAttribute('aria-label') || input.labels?.[0]?.querySelector('span')?.textContent;
    const detail = input.validity.customError ? input.validationMessage : '';
    const message = label ? `Revisa el campo «${label}» antes de continuar.` : 'Revisa los campos indicados antes de continuar.';
    reportProblem(detail ? `${message} ${detail}` : message, 'warning');
    window.requestAnimationFrame(() => input.focus());
  };

  const reportSlugConflict = () => {
    setActiveStep(0);
    reportProblem(`El enlace público «${value.slug}» ya pertenece a otro premio. Cambia el campo «Nombre en el enlace público» para continuar.`, 'warning');
    window.requestAnimationFrame(() => panels.current[0]?.querySelector('[name="slug"]')?.focus());
  };

  const validateStep = (index) => {
    const controls = panels.current[index]?.querySelectorAll('input, select, textarea') || [];
    const invalid = Array.from(controls).find((input) => input.willValidate && !input.validity.valid);
    if (invalid) { invalid.reportValidity(); return false; }
    try {
      if (index === 0) {
        buildPayload(value, false);
        if (!isEditing && templates.some(item => item.slug === value.slug)) {
          reportSlugConflict();
          return false;
        }
      }
      if (index === 2 && (!isEditing || formChanged)) {
        const formError = validateFormSchema(value.form_schema, fileUploadLimits);
        if (formError) throw new Error(formError);
      }
    } catch (validationError) {
      setActiveStep(index);
      reportProblem(apiErrorMessage(validationError), 'warning');
      return false;
    }
    return true;
  };

  const goToStep = (nextStep) => {
    if (busy || nextStep === activeStep) return;
    setError('');
    // Keep unfinished local question drafts mounted and validate them before leaving.
    if (!validateStep(activeStep)) return;
    if (nextStep > activeStep) {
      for (let index = 0; index < nextStep; index += 1) {
        if (!validateStep(index)) return;
      }
    }
    setActiveStep(nextStep);
    window.scrollTo(0, 0);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (activeStep < STEPS.length - 1) { goToStep(activeStep + 1); return; }
    setError('');
    for (let index = 0; index < STEPS.length; index += 1) {
      if (!validateStep(index)) return;
    }
    try {
      const payload = buildPayload(value, !isEditing || formChanged);
      setSaving(true);
      if (isEditing) await PremioAPI.updateConvocatoria(convocatoriaId, payload);
      else await PremioAPI.createConvocatoria(payload);
      setDirty(false);
      showSnackbar(isEditing ? 'Premio actualizado correctamente.' : 'Premio creado correctamente.', 'success');
      history.replace('/premio');
    } catch (saveError) {
      if (saveError?.status === 409 && saveError?.data?.detail === 'Ya existe una convocatoria con ese enlace') {
        reportSlugConflict();
      } else {
        reportProblem(apiErrorMessage(saveError));
      }
      setSaving(false);
    }
  };

  const status = STATUS[value.status] || STATUS.draft;
  const chatEnabled = getApplicationParts(value.settings).includes('chat');
  const publicUrl = `${getPremioWebUrl()}/c/${encodeURIComponent(value.slug)}/login`;

  return (
    <main className="announcement-wizard-page premio-editor">
      <Prompt when={dirty && !saving} message="Hay cambios sin guardar. ¿Quieres salir del editor?" />
      <div className="client__back-container"><BreadCrumb links={[{ url: '/premio', text: 'Premio' }, { text: title, url: '.' }]} /></div>
      <div className="client-container">
        <div className="wizard announcement-wizard">
          <header className="announcement-wizard__hero">
            <div className="announcement-wizard__hero-icon"><EmojiEventsOutlined /></div>
            <div className="announcement-wizard__hero-copy">
              <span className="announcement-wizard__eyebrow">Gestión de premios</span>
              <h1>{title}</h1>
              <p>Configura el inicio de sesión, la identidad visual, el formulario y la evaluación por chat.</p>
            </div>
            {loaded && <span className={`announcement-wizard__status-chip announcement-wizard__status-chip--${status.className}`}>{status.label}</span>}
          </header>
          {loading && <p role="status">Cargando configuración…</p>}
          {!loaded && error && <Alert type={errorSeverity}>{error}</Alert>}
          {!loading && !loaded && <div className="premio-editor__actions">
            <button type="button" className="button secondary" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Reintentar</button>
            <button type="button" className="button secondary" onClick={() => history.push('/premio')}>Volver</button>
          </div>}
          {loaded && <div className="wizard__content">
            <aside className="wizard__steps-container wizard-padding">
              <div className="announcement-wizard__sidebar-heading">
                <span className="announcement-wizard__sidebar-icon"><SettingsOutlined /></span>
                <div><span className="announcement-wizard__eyebrow">Configuración</span><strong>Estado y etapas</strong></div>
              </div>
              <div className="announcement-wizard__status-field">
                <label className="premio-editor__status-label"><span>Estado del premio</span>
                  <select value={value.status} aria-describedby="premio-status-help" disabled={busy} onChange={(event) => change({ status: event.target.value })}>
                    {Object.entries(STATUS).map(([key, item]) => <option key={key} value={key}>{item.label}</option>)}
                  </select>
                </label>
                <p id="premio-status-help" className="premio-editor__help">{status.help}</p>
              </div>
              <div className="announcement-wizard__steps-label">Etapas</div>
              <nav aria-label="Etapas del premio" className="premio-editor__steps">
                {STEPS.map((step, index) => <button key={step.title} type="button" className="premio-editor__step-link" aria-current={index === activeStep ? 'step' : undefined} aria-controls={`premio-step-${index}`} disabled={busy} onClick={() => goToStep(index)}>
                  <Stepper text={step.title} stepNumber={index + 1} isActive={index === activeStep} isDone={index < activeStep} />
                </button>)}
              </nav>
            </aside>
            <div className="wizard__form-container">
              <form className="announcement-form" onSubmit={submit} noValidate onInvalid={revealInvalidField}>
                <div className="announcement-form__toolbar">
                  <div className="announcement-form__toolbar-copy">
                    <span className="announcement-wizard__eyebrow">Etapa {activeStep + 1} de {STEPS.length}</span>
                    <h2 id="premio-current-step">{STEPS[activeStep].title}</h2>
                    <p>{STEPS[activeStep].description}</p>
                  </div>
                  <button type="button" className="button secondary button--flat" disabled={busy} onClick={() => setPreviewOpen(true)}>Previsualizar</button>
                </div>
                {error && <Alert type={errorSeverity}>{error}</Alert>}
                <fieldset disabled={busy} className="premio-editor__fieldset wizard__form">
                  <div id="premio-step-0" data-editor-step="0" className="premio-editor__step-panel" hidden={activeStep !== 0} ref={(node) => { panels.current[0] = node; }}>
                    {!isEditing && <section className="announcement-form__card">
                      <div className="announcement-form__section-header">
                        <span className="announcement-form__section-icon"><FileCopyOutlined /></span>
                        <div><span className="announcement-wizard__eyebrow">Punto de partida</span><h3>Usar una convocatoria como base</h3><p>Opcional: copia sus textos, imágenes y preguntas para crear un nuevo premio.</p></div>
                      </div>
                      <div className="premio-editor__template-row">
                        <label className="premio-editor__field"><span>Copiar estilos y formulario de</span>
                          <select value={templateId} onChange={(event) => setTemplateId(event.target.value)}><option value="">Selecciona una convocatoria</option>{templates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}</select>
                        </label>
                        <button type="button" className="button secondary" disabled={!templateId || busy} onClick={copyTemplate}>Usar configuración</button>
                      </div>
                    </section>}
                    <section className="announcement-form__card">
                      <div className="announcement-form__section-header">
                        <span className="announcement-form__section-icon"><CalendarTodayOutlined /></span>
                        <div><span className="announcement-wizard__eyebrow">Configuración general</span><h3>Vigencia y acceso</h3><p>Define el nombre, el enlace y las fechas de postulación del premio.</p></div>
                      </div>
                      <div className="announcement-form__field-grid announcement-form__field-grid--two">
                        <Field label="Nombre del premio" value={value.title} required maxLength={255} onChange={(nextTitle) => {
                          const autoSlug = !isEditing && (!value.slug || value.slug === slugify(value.title));
                          change({ title: nextTitle, ...(autoSlug ? { slug: slugify(nextTitle) } : {}) });
                        }} />
                        <Field label="Nombre en el enlace público" name="slug" help="Debe ser único para cada premio. Usa letras minúsculas, números y guiones." value={value.slug} required maxLength={100} pattern="[a-z0-9]+(-[a-z0-9]+)*" onChange={(slug) => change({ slug })} />
                        <Field label="Comienza" type="datetime-local" value={value.starts_at} required onChange={(starts_at) => change({ starts_at })} />
                        <Field label="Termina" type="datetime-local" value={value.ends_at} required onChange={(ends_at) => change({ ends_at })} />
                      </div>
                      <div className="premio-editor__notes">
                        <p>Las fechas se muestran en tu zona horaria: {Intl.DateTimeFormat().resolvedOptions().timeZone}.</p>
                        {value.slug && <p>Enlace de postulación: <span>{publicUrl}</span></p>}
                        {isEditing && <p>Si cambias el nombre del enlace, también cambia la dirección de esta convocatoria.</p>}
                      </div>
                    </section>
                    <section className="announcement-form__card">
                      <div className="announcement-form__section-header">
                        <span className="announcement-form__section-icon"><ChatBubbleOutline /></span>
                        <div><span className="announcement-wizard__eyebrow">Etapas de la postulación</span><h3>Formulario y evaluación</h3><p>El Formulario 1 es el primer paso. Incluye el chat evaluador como segundo paso.</p></div>
                      </div>
                      <label className="premio-editor__checkbox">
                        <input type="checkbox" checked={chatEnabled} aria-describedby="premio-chat-help" onChange={(event) => change({ settings: setChatEnabled(value.settings, event.target.checked) })} />
                        <span>Incluir chat evaluador</span>
                      </label>
                      <p id="premio-chat-help" className="premio-editor__hint">{chatEnabled
                        ? 'Después del Formulario 1, las personas deberán completar la evaluación por chat para enviar su postulación.'
                        : 'El premio no incluirá la evaluación por chat.'}</p>
                    </section>
                    <BrandingEditor step="login" value={value.branding} onChange={(branding) => change({ branding })} disabled={busy} onUploading={setUploading} />
                  </div>
                  <div id="premio-step-1" data-editor-step="1" className="premio-editor__step-panel" hidden={activeStep !== 1} ref={(node) => { panels.current[1] = node; }}>
                    <BrandingEditor step="identity" value={value.branding} onChange={(branding) => change({ branding })} disabled={busy} onUploading={setUploading} />
                  </div>
                  <div id="premio-step-2" data-editor-step="2" className="premio-editor__step-panel" hidden={activeStep !== 2} ref={(node) => { panels.current[2] = node; }}>
                    {isEditing && <p className="announcement-question__fixed-note">Las preguntas actualizadas se usarán en nuevas postulaciones. Quienes ya comenzaron conservan su formulario y sus respuestas.</p>}
                    <FormSchemaEditor value={value.form_schema} disabled={busy} fileUploadLimits={fileUploadLimits} onValidationError={(message) => reportProblem(message, 'warning')} onChange={(form_schema) => { change({ form_schema }); setFormChanged(true); }} />
                  </div>
                </fieldset>
                <div className="wizard__action">
                  <button type="button" className="button secondary" disabled={busy} onClick={() => activeStep === 0 ? history.push('/premio') : goToStep(activeStep - 1)}>Volver</button>
                  <button type="submit" className="button" disabled={busy}>{saving ? 'Guardando…' : activeStep < STEPS.length - 1 ? 'Siguiente' : isEditing ? 'Guardar cambios' : 'Crear Premio'}</button>
                </div>
                <span className="premio-editor__save-status" role="status">{uploading ? 'Subiendo archivo…' : saving ? 'Guardando premio…' : dirty ? 'Cambios sin guardar' : ''}</span>
              </form>
            </div>
          </div>}
        </div>
      </div>
      {previewOpen && <PremioPreview open onClose={() => setPreviewOpen(false)} value={value} step={activeStep} />}
    </main>
  );
};

export default ConvocatoriaEditor;
